import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";
import Pagination from "../Pagination/Paginations.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons";
import EntriesSelector from "../Pagination/EntriesSelector";
import Select from 'react-select';
import DatePicker from "react-datepicker";
const AttendanceBriefReport = () => {
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    from_date: today,
    to_date: today,
    dept_id: "",
  });

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);
  const [report, setReport] = useState([]);
  const [dates, setDates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  // 🔹 Fetch departments
  const handleDepartment = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/showdepartment`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setDepartments(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load departments");
    }
  };

  useEffect(() => {
    handleDepartment();
  }, []);

  // 🔹 Handle input change
  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

 const handleSubmit = async (e) => {
  e.preventDefault();

  // block repeat clicks while a request is already running
  if (loadingRef.current) return;
  loadingRef.current = true;
  setLoading(true);

  try {
    const res = await axios.post(
      `${API_BASE_URL}/attendanceBrief`,
      formData,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      }
    );

    setReport(Object.values(res.data.records || {}));
    setDates(res.data.dates || []);
    setCurrentPage(1);
  } catch (err) {
    setReport([]);
    setDates([]);
    toast.error("Failed to fetch report", { toastId: "attendance-brief-error" });
  } finally {
    loadingRef.current = false;
    setLoading(false);
  }
};

  // 🔹 Day column header — same format as the payroll report (d-m)
  const dayLabel = (date) => {
    const [, m, d] = date.split("-");
    return `${d}-${m}`;
  };

  // 🔹 Generate flattened data for CSV/PDF download
  const generateFlatData = () => {
    return report.map((emp) => {
      const s = emp.summary || {};

      const flatAttendance = {};
      dates.forEach((date) => {
        flatAttendance[dayLabel(date)] = emp.attendance?.[date]?.status ?? "";
      });

      return {
        "Emp ID": emp.employee_id,
        "Employee Name": emp.employee_name,
        Department: emp.department,
        "Date of Joining": emp.date_of_joining,
        ...flatAttendance,
        Present: s.present,
        Holidays: s.holidays,
        Absent: s.absent,
        "Week Off": s.weekoff,
        "CL Leave": s.cl_leave,
        "SL Leave": s.sl_leave,
        LOP: s.lop,
        "MAT Leave": s.mat_leave,
        "PL Leave": s.pl_leave,
        "HD Leave": s.hd_leave,
        "Payable Days": s.payable_days,
        "Available CL": emp.leave_available,
        "Available SL": emp.sick_leave_available,
        "Manual Update": s.manual,
      };
    });
  };

  // 🔹 Search filter (applied before paging so every page is searchable)
  const filteredRecords = report.filter((emp) =>
    (emp.employee_name || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 🔹 Pagination logic
  const totalPages = Math.ceil(filteredRecords.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirst, indexOfLast);

  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />
      <div className="card shadow">
        <div className="card-header">
          <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
              <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
              <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
              <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
            </svg>
            Attendance Brief Report
          </h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-3">
  <label>From Date</label>

  <DatePicker
    selected={formData.from_date ? new Date(formData.from_date) : null}
    onChange={(date) => {
      const value = date
        ? date.toLocaleDateString("en-CA")
        : "";

      setFormData((prev) => ({ ...prev, from_date: value }));
    }}
    dateFormat="yyyy-MM-dd"
    placeholderText="Select From Date"
    className="form-control"
    maxDate={formData.to_date ? new Date(formData.to_date) : null}
  />
</div>

            <div className="col-md-3">
  <label>To Date</label>

  <DatePicker
    selected={formData.to_date ? new Date(formData.to_date) : null}
    onChange={(date) => {
      const value = date
        ? date.toLocaleDateString("en-CA")
        : "";

      setFormData((prev) => ({ ...prev, to_date: value }));
    }}
    dateFormat="yyyy-MM-dd"
    placeholderText="Select To Date"
    className="form-control"
    minDate={formData.from_date ? new Date(formData.from_date) : null}
  />
</div>

            <div className="col-md-3">
              <label>Department</label>
               <Select
    name="dept_id"
    value={departments
      .map((dept) => ({
        value: dept.department_id,
        label: dept.department_name,
      }))
      .find((d) => d.value === formData.dept_id) || null}
    onChange={(selected) =>
      handleChange({
        target: { name: "dept_id", value: selected ? selected.value : "" },
      })
    }
    options={departments.map((dept) => ({
      value: dept.department_id,
      label: dept.department_name,
    }))}
    isClearable
    placeholder="All"
    className="basic-single"
  />
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <button className="btn btn-primary btn-sm" type="submit" style={{ height: "38px" }} disabled={loading}>
                {loading ? "Loading..." : "Get"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 🔹 Controls */}
      <div
  className="d-flex flex-wrap align-items-center justify-content-between my-3"
  style={{ gap: "1rem" }}
>
  {/* Left section — CSV/PDF download + Show entries */}
  <div className="d-flex align-items-center gap-3">
   <DownloadButtons data={generateFlatData()} filename="Attendance_Brief_Report" />
  </div>

  {/* Right section — Show entries + Search bar */}
  <div className="d-flex align-items-center" style={{ gap: "10px" }}>
    <EntriesSelector
      entriesPerPage={entriesPerPage}
      setEntriesPerPage={setEntriesPerPage}
      setCurrentPage={setCurrentPage}
    />
    <div style={{ width: "250px" }}>
      <input
        type="text"
        placeholder="Search employee..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
        className="form-control"
      />
    </div>
  </div>
</div>


      {/* 🔹 Report Table */}
      <div className="card mt-4">
        <div className="card-body table-responsive sticky-table-wrap">
          {loading && <p className="text-center text-info">Loading...</p>}

          {!loading && report.length > 0 ? (
            <table className="table table-bordered text-center sticky-table sticky-head-tint sticky-no-freeze-col">
              <thead>
                <tr
                  
                >
                  <th>Emp ID</th>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>DOJ</th>

                  {dates.map((d, i) => (
                    <th key={i}>{dayLabel(d)}</th>
                  ))}

                  <th>Present</th>
                  <th>Holidays</th>
                  <th>Absent</th>
                  <th>Week Off</th>
                  <th>CL Leave</th>
                  <th>SL Leave</th>
                  <th>LOP</th>
                  <th>MAT Leave</th>
                  <th>PL Leave</th>
                  <th>HD Leave</th>
                  <th>Payable Days</th>
                  <th>Available CL</th>
                  <th>Available SL</th>
                  <th>Manual Updates</th>
                </tr>
              </thead>

              <tbody>
                {currentRecords.map((emp, i) => {
                  const s = emp.summary || {};

                  return (
                    <tr key={emp.employee_id ?? i}>
                      <td>{emp.employee_id}</td>
                      <td>{emp.employee_name}</td>
                      <td>{emp.department}</td>
                      <td>{emp.date_of_joining}</td>

                      {dates.map((date, j) => {
                        const cell = emp.attendance?.[date];

                        // no row for the day (e.g. employee not in that shift plan)
                        if (!cell) return <td key={j}></td>;

                        // before date of joining
                        if (cell.before_doj)
                          return (
                            <td key={j} style={{ color: "#999" }}>
                              -
                            </td>
                          );

                        // present marked through a manual attendance update
                        if (cell.manual)
                          return (
                            <td key={j}>
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: "3px",
                                  backgroundColor: "#198754",
                                  color: "#fff",
                                  fontWeight: 600,
                                }}
                                title="Manually updated"
                              >
                                {cell.status}
                              </span>
                            </td>
                          );

                        return <td key={j}>{cell.status}</td>;
                      })}

                      <td>{s.present}</td>
                      <td>{s.holidays}</td>
                      <td>{s.absent}</td>
                      <td>{s.weekoff}</td>
                      <td>{s.cl_leave}</td>
                      <td>{s.sl_leave}</td>
                      <td>{s.lop}</td>
                      <td>{s.mat_leave}</td>
                      <td>{s.pl_leave}</td>
                      <td>{s.hd_leave}</td>
                      <td>{s.payable_days}</td>
                      <td>{emp.leave_available}</td>
                      <td>{emp.sick_leave_available}</td>
                      <td>{s.manual}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            !loading && (
              <p className="text-center text-muted">No records found</p>
            )
          )}
        </div>
      </div>
       <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
    </div>
  );
};

export default AttendanceBriefReport;
